import { type Routine, useStores } from '@littlebird/sdk';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useEffect } from 'react';
import { toast } from 'sonner';

import { ConfirmDialog } from '@/components/modals/ConfirmationModal';
import { ModalsManager } from '@/components/modals/modalsManager';
import { getRoutineActionsWithLatestRun } from '@/features/routines/routine-action-state';
import { useSubscription } from '@/features/subscription/subscription.query';
import { trackEvent } from '@/utils/analytics';
import { debugToast } from '@/utils/toast';

import { routinesStore } from '../routines.state';

export const useDeleteRoutine = () => {
  const { routines, routineDrafts, reports } = useStores();
  const subscription = useSubscription();
  const navigate = useNavigate();

  const deleteRoutine = async (routineId: number) => {
    try {
      await routines.deleteRoutine(routineId);
      routineDrafts.clearRoutine(routineId);
      subscription.refetch();
      navigate({ to: '/routines' });
    } catch (error) {
      toast.error('Failed to delete routine, please try again');
      debugToast(error instanceof Error ? error.message : String(error));
    }
  };

  const showModal = async (routine: Routine) => {
    const { waitingFor } = getRoutineActionsWithLatestRun(routine, reports);
    const waitingRunNote = waitingFor
      ? ` A run is waiting for ${waitingFor}; it is cancelled with the routine.`
      : '';
    const confirmed = await ModalsManager.showResolvable(ConfirmDialog, {
      title: 'Delete this routine?',
      description: `This will permanently delete “${routine.title}” and all of its previous runs. This action cannot be undone.${waitingRunNote}`,
      confirmText: 'Delete routine',
      confirmVariant: 'destructive',
      cancelText: 'Cancel',
    });
    if (!confirmed) return false;

    trackEvent('ROUTINE_DELETED', { routineId: routine.referenceId });
    void deleteRoutine(routine.referenceId);
    return true;
  };

  return { deleteRoutine, showModal };
};

export const useTrackPageViews = () => {
  const params = useParams({ strict: false });
  const routineId = params.routineId ? Number(params.routineId) : null;
  const selectedReportId = routinesStore.useSelectedReportId(routineId);

  useEffect(() => {
    if (!selectedReportId) return;

    trackEvent('ROUTINE_REPORT_VIEWED', {
      reportId: selectedReportId,
    });
  }, [selectedReportId]);

  useEffect(() => {
    if (!routineId) return;

    trackEvent('ROUTINE_PAGE_VIEWED', {
      reportId: routineId,
    });
  }, [routineId]);
};
